import { generatePath } from 'react-router-dom';
import request from '../api/index';
import PaginationResponseDto from '../interfaces/paginationResponse.interface';
import { API_ROUTES } from '../consts';
import { RequestQueryBuilderParamsEnum } from '../consts/enums';
import { buildOrder, buildRequestParamsByType, buildSelect } from '../utils/request';
import {
  Account,
  AccountDeleteRequest,
  AccountPlan,
  AccountRequest,
  AccountUpdateAssociated,
  AccountWithFeatures,
  AddCreditsRequest,
  AddUserSeatsRequest,
  CreateOrUpdateAccountPlanDto,
  GetAccountPlanDefinitionsRequest,
  GetCreditAllocationsResponse,
  GetCreditsResponse,
  PlanDefinition,
  UpdateAccountRequest,
} from '../@types/Account';

export type AccountsQueryType = {
  page?: number,
  limit?: number,
  contains?: {
    [key: string]: string,
  },
  select?: string[],
  order?: Sort,
  in?: {
    [key: string]: string[],
  }
  exact? : {
    [key: string]: string,
  }
  not? : {
    [key: string]: string,
  }
}

export const getAccounts = async (
  query: AccountsQueryType,
): Promise<PaginationResponseDto<Account>>  => {
  const { data } = await request.get(API_ROUTES.ACCOUNTS, {
    params: {
      ...(query.exact || {}),
      ...buildRequestParamsByType(query.not || {}, RequestQueryBuilderParamsEnum.not),
      ...buildRequestParamsByType(query.contains || {}, RequestQueryBuilderParamsEnum.contains),
      select: buildSelect(query.select),
      page: query.page,
      limit: query.limit,
      order: buildOrder(query.order || {}),
    },
  });
  const {
    results, page, totalCount, limit,
  } = data;

  return {
    results,
    page,
    totalCount,
    limit,
  };
};

export const getAccount = async (
  accountId: string,
): Promise<Account>  => {
  const path = generatePath(API_ROUTES.ACCOUNT, { accountId });

  const { data } = await request.get(path);

  return data;
};

export const deleteAccount = async ({
  accountId,
}: AccountDeleteRequest): Promise<void> => {
  const path = API_ROUTES.ACCOUNTS;

  return request.delete(`${path}/${accountId}`);
};

export const createAccount = async (newAccount: AccountRequest):Promise<Account> => {
  const { data } = await request.post(API_ROUTES.ACCOUNTS, newAccount);

  return data;
};

export const updateAccount = async (newAccount: UpdateAccountRequest, id: string)
:Promise<AccountUpdateAssociated> => {
  const path = generatePath(API_ROUTES.ACCOUNT, { accountId: id });
  const { data } = await request.patch(path, newAccount);

  return data;
};

export const getAccountsInfo = async (
  query: AccountsQueryType,
): Promise<PaginationResponseDto<Account>>  => {
  const { data } = await request.get(API_ROUTES.ACCOUNTS_INFO, {
    params: {
      ...buildRequestParamsByType(query.contains || {}, RequestQueryBuilderParamsEnum.contains),
      ...buildRequestParamsByType(query?.in || {}, RequestQueryBuilderParamsEnum.in),
      select: buildSelect(query.select),
      page: query.page,
      limit: query.limit,
      order: buildOrder(query.order || {}),
    },
  });
  const {
    results, page, totalCount, limit,
  } = data;

  return {
    results,
    page,
    totalCount,
    limit,
  };
};

export const getAccountByUserEmail = async (
  email:string,
): Promise<Account>  => {
  const path = generatePath(API_ROUTES.ACCOUNTS_BY_USER_EMAIL, { email });

  const { data } = await request.get(path);

  return data;
};

export const getActiveAccountsWithFeatures = async (
  query: AccountsQueryType,
): Promise<PaginationResponseDto<AccountWithFeatures>>  => {
  const { data } = await request.get(API_ROUTES.ACTIVE_ACCOUNTS, {
    params: {
      ...buildRequestParamsByType(query.contains || {}, RequestQueryBuilderParamsEnum.contains),
      ...buildRequestParamsByType(query?.in || {}, RequestQueryBuilderParamsEnum.in),
      select: buildSelect(query.select),
      page: query.page,
      limit: query.limit,
      order: buildOrder(query.order || {}),
    },
  });
  const {
    results, page, totalCount, limit,
  } = data;

  return {
    results,
    page,
    totalCount,
    limit,
  };
};

export const uploadImageAccount = async (formData:FormData | null):Promise<{fileUrl: string}> => {
  const config = {
    headers: {
      'content-type': 'multipart/form-data',
    },
  };
  const { data } = await request.post(API_ROUTES.ACCOUNT_IMAGE_UPLOAD, formData, config);

  return data;
};

export const updateAccountPlan = async (
  accountId: string,
  accountPlanId: string,
  plan: Partial<CreateOrUpdateAccountPlanDto>,
):Promise<AccountPlan> => {
  const path = generatePath(API_ROUTES.ACCOUNT_PLAN, { accountId, accountPlanId });
  const { data } = await request.patch(path, plan);

  return data;
};

export const createAccountPlan = async (
  accountId: string,
  plan: CreateOrUpdateAccountPlanDto,
):Promise<AccountPlan> => {
  const path = generatePath(API_ROUTES.ACCOUNT_PLANS, { accountId });
  const { data } = await request.post(path, plan);

  return data;
};

export const addUserSeats = async (
  accountId: string,
  payload: AddUserSeatsRequest,
):Promise<void> => {
  const path = generatePath(API_ROUTES.ACCOUNT_PLAN_USER_SEATS, { accountId });
  await request.post(path, payload);
};

export const addCredits = async (
  accountId: string,
  payload: AddCreditsRequest,
):Promise<void> => {
  const path = generatePath(API_ROUTES.ACCOUNT_PLAN_CREDITS, { accountId });
  await request.post(path, payload);
};

export const getCredits = async (
  accountId: string,
):Promise<GetCreditsResponse> => {
  const path = generatePath(API_ROUTES.ACCOUNT_PLAN_CREDITS, { accountId });
  const { data } = await request.get(path);
  
  return data;
};

export const getCreditAllocations = async (
  accountId: string,
):Promise<GetCreditAllocationsResponse> => {
  const path = generatePath(`${API_ROUTES.ACCOUNT_PLAN_CREDITS}/allocations`, { accountId });
  const { data } = await request.get(path);

  return data;
};

export const getAccountPlanDefinitions = async (payload?: GetAccountPlanDefinitionsRequest)
  : Promise<PlanDefinition[]> => {
  const { data } = await request.get(API_ROUTES.ACCOUNT_PLAN_DEFINITIONS, { params: payload });

  return data;
};

export const deleteAccountPlan = async (
  accountId: string,
  accountPlanId: string,
): Promise<void> => {
  const path = generatePath(API_ROUTES.ACCOUNT_PLAN, { accountId, accountPlanId });
  const { data } = await request.delete(path);

  return data;
};
